import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './FlashcardModePage.css';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabaseClient';
import { useData } from '../contexts/DataContext';

const FlashcardModePage = () => {
  const { id, groupId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { modules } = useData();

  const [moduleData, setModuleData] = useState(null);
  const [groupData, setGroupData] = useState(null);
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchFlashcardData = async () => {
      // Get user session
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      
      if (!userId) {
        setLoading(false);
        return;
      }

      // First, try to find the module from dashboard data (minimal info)
      let currentMod = modules?.find(m => String(m.id) === String(id));

      // If not found in global state (e.g., on refresh or for classroom members), fetch it directly from Supabase
      if (!currentMod) {
        // Check if id is a UUID (contains dashes) or a position (number)
        const isUUID = typeof id === 'string' && id.includes('-');
        
        let query = supabase
          .from('modules_new')
          .select('*');
        
        if (isUUID) {
          // It's a module ID (classroom module) - RLS will handle access check
          query = query.eq('id', id);
        } else {
          // It's a position (private module)
          query = query.eq('position', Number(id))
                      .eq('user_id', userId);
        }
        
        const { data: moduleData, error: moduleError } = await query.single();

        if (moduleError) {
          console.error('Error fetching module:', moduleError);
          setLoading(false);
          return;
        }

        currentMod = moduleData;
      }

      if (!currentMod) {
        setLoading(false);
        return;
      }

      // Check access for classroom modules
      if (currentMod.classroom_id) {
        const { checkModuleAccess } = await import('../utils/checkModuleAccess');
        const accessCheck = await checkModuleAccess(currentMod.id, currentMod.classroom_id);
        if (!accessCheck.canAccess) {
          setError(accessCheck.error || 'Access to this module is blocked');
          setLoading(false);
          return;
        }
      }
      
      setModuleData(currentMod);

      try {
        // Fetch word group by ID directly (groupId is now the UUID)
        const { data: groupData, error: groupError } = await supabase
          .from('word_groups')
          .select('*')
          .eq('module_id', currentMod.id)
          .eq('id', groupId)
          .single();
        
        if (groupError) {
          console.error('Error fetching word group:', groupError);
          setLoading(false);
          return;
        }
        
        setGroupData(groupData);

        // Fetch words for this group with user's SRS data
        const { data: wordsData, error: wordsError } = await supabase
          .from('words')
          .select(`
            id,
            group_id,
            from_text,
            to_text,
            position,
            created_at,
            updated_at,
            word_srs_data!left (
              sm2_interval,
              learning_interval,
              sm2_ease,
              learning_ease,
              next_due,
              apparent_tier,
              history,
              updated_at
            )
          `)
          .eq('group_id', groupData.id)
          .eq('word_srs_data.user_id', userId)
          .order('position', { ascending: true });
        
        if (wordsError) {
          console.error('Error fetching words:', wordsError);
          setLoading(false);
          return;
        }

        // Merge word data with SRS data
        const mergedWords = (wordsData || []).map(word => {
          const srs = word.word_srs_data?.[0] || null;
          return {
            ...word,
            word_srs_data: undefined,
            sm2_interval: srs?.sm2_interval ?? 0,
            learning_interval: srs?.learning_interval ?? 0,
            ease: srs?.sm2_ease ?? 2.5,
            next_due: srs?.next_due ?? 0,
            apparent_tier: srs?.apparent_tier ?? 1,
            history: srs?.history 
              ? (typeof srs.history === 'string' ? JSON.parse(srs.history) : srs.history)
              : [],
            srs_updated_at: srs?.updated_at || word.updated_at
          };
        });

        setWords(mergedWords);
      } catch (error) {
        console.error('Error loading flashcard data:', error);
      }
      
      setLoading(false);
    };

    fetchFlashcardData();
  }, [id, groupId, modules, navigate]);

  if (loading) {
    return <div className="flashcard-mode-container">{t('loading') || 'Loading...'}</div>;
  }

  if (error) {
    return (
      <div className="flashcard-mode-container">
        <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
          <h2>{t('accessDenied') || 'Access Denied'}</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="btn btn-primary">
            {t('backToDashboard') || 'Back to Dashboard'}
          </button>
        </div>
      </div>
    );
  }

  if (!moduleData || !groupData) {

    return <div className="flashcard-mode-container">{t('wordGroupNotFound')}</div>;
  }

  const startFlashcards = (mode) => {
    navigate(`/module/${moduleData.id}/group/${groupData.id}/flashcards?mode=${mode}`, {
      state: { mode, groupWords: words }
    });
  };

  return (
    <div className="flashcard-mode-container">
      <div className="flashcard-mode-content">
        <button 
          onClick={() => navigate(`/module/${moduleData.id}/group/${groupData.id}`)} 
          className="btn btn-secondary btn-back"
        >
          {t('backToModule')}
        </button>
        
        <div className="mode-selection-card">
          <h1>{t('chooseFlashcardMode')}</h1>
          <p className="mode-description">
            {t('selectHowToPractice')}
          </p>
          
          <div className="mode-options">
            <button 
              className="mode-option learning-mode" 
              onClick={() => startFlashcards('learning')}
              aria-label={t('startLearningMode')}
            >
              <div className="mode-icon">🎯</div>
              <h3>{t('learningMode')}</h3>
              <p className="mode-details">
                {t('learningModeDescription')}
              </p>
              <ul className="mode-features">
                <li>{t('learningModeFeature1')}</li>
                <li>{t('learningModeFeature2')}</li>
                <li>{t('learningModeFeature3')}</li>
                <li>{t('learningModeFeature4')}</li>
              </ul>
              <span className="btn btn-primary mode-btn">{t('startLearningMode')}</span>
            </button>
            
            <button 
              className="mode-option classic-mode" 
              onClick={() => startFlashcards('classic')}
              aria-label={t('startClassicMode')}
            >
              <div className="mode-icon">📚</div>
              <h3>{t('classicMode')}</h3>
              <p className="mode-details">
                {t('classicModeDescription')}
              </p>
              <ul className="mode-features">
                <li>{t('classicModeFeature1')}</li>
                <li>{t('classicModeFeature2')}</li>
                <li>{t('classicModeFeature3')}</li>
                <li>{t('classicModeFeature4')}</li>
              </ul>
              <span className="btn btn-secondary mode-btn">{t('startClassicMode')}</span>
            </button>

            <button 
              className="mode-option review-due-mode" 
              onClick={() => startFlashcards('sm2')}
              aria-label={t('reviewDueWords')}
            >
              <div className="mode-icon">⏰</div>
              <h3>{t('reviewDueWords')}</h3>
              <p className="mode-details">
                {t('reviewDueWordsDescription')}
              </p>
              <ul className="mode-features">
                <li>{t('reviewDueWordsFeature1')}</li>
                <li>{t('reviewDueWordsFeature2')}</li>
                <li>{t('reviewDueWordsFeature3')}</li>
              </ul>
              <span className="btn btn-warning mode-btn">{t('startReview')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlashcardModePage;
